import React, { useState, useEffect } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, CircularProgress, Typography, Chip, TextField, MenuItem, Grid, IconButton, Tooltip
} from '@mui/material';
import { Refresh as RefreshIcon, Visibility as ViewIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { getRepairs, getRepairInvoices, deleteRepair } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { canDelete } from '../../utils/auth';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportDate, fmtExportMoney, fmtExportStatus } from '../../utils/tableExport';

const statusColor = (status) => {
  const map = {
    pending: 'warning', diagnosis: 'info', quoted: 'primary', approved: 'primary',
    in_progress: 'secondary', completed: 'success', delivered: 'success', cancelled: 'error'
  };
  return map[status] || 'default';
};

const RepairInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const allowDelete = canDelete();

  useEffect(() => { loadData(); }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const statusQ = statusFilter ? `&status=${statusFilter}` : '';
      let res;
      try {
        res = await getRepairInvoices(`?limit=200${statusQ}`);
      } catch {
        res = await getRepairs(`?limit=200${statusQ}`);
      }
      setInvoices(res.data || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load repair invoices'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (inv) => {
    if (!allowDelete || !inv?.id) return;
    const label = inv.invoice_number || inv.service_number || inv.repair_number || `#${inv.id}`;
    if (!window.confirm(`Delete repair invoice / job ${label}?\n\nThis permanently deletes the service request and cannot be undone.`)) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      const res = await deleteRepair(inv.id);
      if (res?.success === false) {
        setError(res.message || 'Failed to delete');
        return;
      }
      setSuccess(`Deleted ${label}`);
      setTimeout(() => setSuccess(''), 3000);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete repair invoice'));
    } finally {
      setLoading(false);
    }
  };

  const filtered = invoices.filter((inv) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (inv.service_number || '').toLowerCase().includes(q) ||
      (inv.repair_number || '').toLowerCase().includes(q) ||
      (inv.invoice_number || '').toLowerCase().includes(q) ||
      (inv.customer_name || '').toLowerCase().includes(q) ||
      (inv.device_serial || '').toLowerCase().includes(q)
    );
  });

  const fmt = (v) => `Rs. ${parseFloat(v || 0).toFixed(2)}`;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>Repair Invoices</Typography>
          <Typography color="text.secondary">View all repair job invoices and payment status</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <ExportButtons
            title="Repair Invoices"
            filename="repair_invoices"
            landscape
            columns={[
              { label: 'Service #', value: (inv) => dash(inv.service_number || inv.repair_number || (inv.id ? `#${inv.id}` : '')) },
              { label: 'Customer', value: (inv) => dash(inv.customer_name) },
              { label: 'Device', value: (inv) => [inv.device_brand, inv.device_model].filter(Boolean).join(' ') || '-' },
              { label: 'Parts', align: 'right', value: (inv) => fmtExportMoney(inv.parts_cost) },
              { label: 'Total', align: 'right', value: (inv) => fmtExportMoney(inv.total_amount) },
              { label: 'Paid', align: 'right', value: (inv) => fmtExportMoney(inv.deposit_paid || inv.amount_paid) },
              { label: 'Balance', align: 'right', value: (inv) => fmtExportMoney(inv.balance_due) },
              { label: 'Status', value: (inv) => fmtExportStatus(inv.status || 'pending') },
              { label: 'Date', value: (inv) => fmtExportDate(inv.created_at) }
            ]}
            rows={filtered}
            onError={setError}
          />
          <Button startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {allowDelete && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Super admin can delete invoices (removes the full service request).
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <TextField fullWidth label="Search" placeholder="Service #, customer, serial..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField select fullWidth label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="diagnosis">Diagnosis</MenuItem>
            <MenuItem value="quoted">Quoted</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="delivered">Delivered</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Service #</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Parts</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Paid</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell><strong>{inv.service_number || inv.repair_number || `#${inv.id}`}</strong></TableCell>
                  <TableCell>{inv.customer_name || '-'}</TableCell>
                  <TableCell>{inv.device_brand} {inv.device_model}</TableCell>
                  <TableCell>{fmt(inv.parts_cost)}</TableCell>
                  <TableCell><strong>{fmt(inv.total_amount)}</strong></TableCell>
                  <TableCell>{fmt(inv.deposit_paid || inv.amount_paid)}</TableCell>
                  <TableCell>{fmt(inv.balance_due)}</TableCell>
                  <TableCell><Chip label={inv.status || 'pending'} size="small" color={statusColor(inv.status)} /></TableCell>
                  <TableCell>{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    <Button size="small" component={Link} to={`/repairs/${inv.id}`} startIcon={<ViewIcon />}>View</Button>
                    {allowDelete && (
                      <Tooltip title="Delete (Super Admin)">
                        <IconButton size="small" color="error" onClick={() => handleDelete(inv)} sx={{ ml: 0.5 }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={11} align="center">No repair invoices found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RepairInvoices;
